/*
 * File: AllPrefs.ts
 * Project: mp-mobile-app
 *
 * Created by Cole on 06/17/2021
 *
 * Copyright (c) 2022 MacPractice, Inc. All rights reserved.
 *
 * Description: Defines the set of preferences fetched during preloading
 */
import { types, IType, SnapshotIn } from "mobx-state-tree";
import { AllowedTextFormatPreference } from "./AllowedTextFormat";
import { AppointmentFiltersPreference } from "./AppointmentFiltersPreference";
import { BoolPreference } from "./BoolPreference";
import { ClinicalPreferences } from "./ClinicalPreferences";
import { IntParsePreference } from "./IntParsePreference";
import { OrderFiltersPreference } from "./OrderFiltersPreference";
import { PreferenceBase } from "./PreferenceBase";
import { RecentPatientsPreference } from "./RecentPatientsPreference";
import { StringPreference } from "./StringPreference";

const prefTypes = {
  string: (pkey: string, key?: string) =>
    prefTypes.other(StringPreference, pkey, key),
  bool: (pkey: string, key?: string) =>
    prefTypes.other(BoolPreference, pkey, key),
  parseInt: (pkey: string, key?: string) =>
    prefTypes.other(IntParsePreference, pkey, key),
  other: <S, T>(
    type: IType<SnapshotIn<typeof PreferenceBase>, S, T>,
    pkey: string,
    key?: string
  ) => types.optional(type, () => type.create({ pkey, internalKey: key }))
};

/**
 * A model containing all of the preference values that must be loaded at login.
 *
 * The preference keys are duplicated in the definition object as a key and in the type definition value. Ideally we could just add them to the map dynamically to avoid repeating the key twice, but this makes the typescript definition of the type incomplete.
 */
export const AllPrefs = types.model("AllPrefs", {
  ["coding.problems"]: prefTypes.string("coding.problems"),
  ["coding.medications"]: prefTypes.string("coding.medications"),
  ["coding.billingDiagnoses"]: prefTypes.string("coding.billingDiagnoses"),
  ["coding.enounterDiagnoses"]: prefTypes.string("coding.enounterDiagnoses"),
  ["coding.allergies"]: prefTypes.string("coding.allergies"),
  ["coding.ePrescribeDiagnoses"]: prefTypes.string(
    "coding.ePrescribeDiagnoses"
  ),
  ["ledger.enableToDateBeforeCode"]: prefTypes.bool(
    "ledger.enableToDateBeforeCode"
  ),
  ["localization.dateFormat"]: prefTypes.other(
    AllowedTextFormatPreference,
    "localization.dateFormat"
  ),
  ["localization.useReferralsExpirationDate"]: prefTypes.bool(
    "localization.useReferralsExpirationDate"
  ),
  ["localization.useCanadianClaimFields"]: prefTypes.bool(
    "localization.useCanadianClaimFields"
  ),
  ["localization.phoneFormat"]: prefTypes.other(
    AllowedTextFormatPreference,
    "localization.phoneFormat"
  ),
  ["localization.stateFormat"]: prefTypes.other(
    AllowedTextFormatPreference,
    "localization.stateFormat"
  ),
  ["localization.ssnMedicare"]: prefTypes.string("localization.ssnMedicare"),
  ["localization.zipCodeFormat"]: prefTypes.other(
    AllowedTextFormatPreference,
    "localization.zipCodeFormat"
  ),
  ["localization.temperature"]: prefTypes.string("localization.temperature"),
  ["localization.headCircumference"]: prefTypes.string(
    "localization.headCircumference"
  ),
  ["orders.defaultOrderType"]: prefTypes.parseInt("orders.defaultOrderType"),
  ["scheduling.calendar.startTime"]: prefTypes.string(
    "scheduling.calendar.startTime"
  ),
  ["scheduling.calendar.endTime"]: prefTypes.string(
    "scheduling.calendar.endTime"
  ),
  ["scheduling.appointments.allowAppointmentOverlaps"]: prefTypes.bool(
    "scheduling.appointments.allowAppointmentOverlaps"
  ),
  ["scheduling.appointments.showOverlapIndicator"]: prefTypes.bool(
    "scheduling.appointments.showOverlapIndicator"
  ),
  ["scheduling.appointments.createAccountAlerts"]: prefTypes.bool(
    "scheduling.appointments.createAccountAlerts"
  ),
  ["scheduling.appointments.defaultStatus"]: prefTypes.string(
    "scheduling.appointments.defaultStatus"
  ),
  ["scheduling.appointments.defaultType"]: prefTypes.parseInt(
    "scheduling.appointments.defaultType"
  ),
  ["security.rememberLogin"]: prefTypes.bool("security.rememberLogin"),
  allowDx: prefTypes.bool("billing.allowDx", "allowDx"),
  allowModifiers: prefTypes.bool("billing.allowModifiers", "allowModifiers"),
  allowToothSurface: prefTypes.bool(
    "billing.allowToothSurface",
    "allowToothSurface"
  ),
  defaultView: prefTypes.parseInt("general.defaultView", "defaultView"),
  autoLogout: prefTypes.parseInt("security.mpMobileAutoLogout", "autoLogout"),
  apptColors: prefTypes.bool(
    "scheduling.appointments.showAppointmentColors",
    "apptColors"
  ),
  defaultSchedulingView: prefTypes.string(
    "general.defaultSchedulingView",
    "defaultSchedulingView"
  ),
  defaultOrdersView: prefTypes.string(
    "general.defaultOrdersView",
    "defaultOrdersView"
  ),
  recentPatients: prefTypes.other(
    RecentPatientsPreference,
    "patientSelector.recentPatients",
    "recentPatients"
  ),
  apptFilters: prefTypes.other(
    AppointmentFiltersPreference,
    "scheduling.appointments.mpMobileFilters",
    "apptFilters"
  ),
  ordersFilters: prefTypes.other(
    OrderFiltersPreference,
    "orders.mpMobileFilters",
    "ordersFilters"
  ),
  clinicalSummary: prefTypes.other(
    ClinicalPreferences,
    "clinical.relevantSummaryForWeb",
    "clinicalSummary"
  )
});
